import { useState, useEffect, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Smartphone, RefreshCw, Copy, Check } from 'lucide-react'
import { auth } from '../api/client'

const EXPIRY_SECONDS = 5 * 60 // 5 minutes

export default function LinkDevice() {
  const [code, setCode] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [copied, setCopied] = useState(false)

  const generateMutation = useMutation({
    mutationFn: () => auth.createLinkCode(),
    onSuccess: (data) => {
      setCode(data.code)
      setSecondsLeft(EXPIRY_SECONDS)
      setCopied(false)
    },
  })

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setCode(null)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [secondsLeft])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handleCopy = useCallback(() => {
    if (code) {
      navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [code])

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
          <Smartphone size={24} className="text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Link a Device</h1>
          <p className="text-dark-400 text-sm">Sign in on another device using a link code</p>
        </div>
      </div>

      <div className="card">
        {!code ? (
          <div className="text-center py-8">
            <p className="text-dark-300 mb-6">
              Generate a 4-character code to sign in on your Android device without typing your password.
            </p>
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="btn btn-primary px-8 py-3 text-lg"
            >
              {generateMutation.isPending ? (
                <RefreshCw size={20} className="animate-spin" />
              ) : (
                'Generate Link Code'
              )}
            </button>
            {generateMutation.error && (
              <p className="mt-4 text-red-400 text-sm">
                Failed to generate code. Please try again.
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-dark-300 mb-4">
              Enter this code on your Android device
            </p>

            {/* Large code display */}
            <div
              className="inline-flex items-center gap-2 bg-dark-800 rounded-2xl px-10 py-6 mb-4 cursor-pointer hover:bg-dark-700 transition-colors"
              onClick={handleCopy}
              title="Click to copy"
            >
              <span className="text-5xl sm:text-6xl font-mono font-bold tracking-[0.3em] text-white">
                {code}
              </span>
              <button className="ml-2 text-dark-400 hover:text-white transition-colors">
                {copied ? <Check size={24} className="text-green-400" /> : <Copy size={24} />}
              </button>
            </div>

            {/* Timer */}
            <p className="text-dark-400 mb-6">
              Expires in <span className="text-white font-mono">{formatTime(secondsLeft)}</span>
            </p>

            {/* Instructions */}
            <div className="text-left max-w-sm mx-auto mb-6 space-y-2 text-dark-300 text-sm">
              <p>1. Open S3nd Media on your Android device</p>
              <p>2. Tap <strong className="text-white">Link Device</strong> on the login screen</p>
              <p>3. Enter the code above</p>
            </div>

            {/* Regenerate button */}
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="btn btn-secondary px-6 py-2"
            >
              <RefreshCw size={16} className={generateMutation.isPending ? 'animate-spin' : ''} />
              Generate New Code
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
